'use client';

import { RefCallback } from 'react';

import {
    Box,
    BoxContent,
    Button,
    ButtonMarkup,
    ButtonType,
    ButtonVariant,
    Column,
    Columns,
    Divider,
    Row,
    Rows,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TypographyBold,
} from '@bytel/trilogy-react';

import { getFamilyContract } from '@services/mcube';
import { filterPromotionsByTypesMarketing } from '@services/products';

import { PlanType } from '@app-types/plan';
import { Product } from '@app-types/product';
import { ImpactsSimulation } from '@app-types/simulator/simulator';
import { SimulatedCart, SimulatorCart } from '@app-types/simulator/virtual-cart';

import { CurrentContract } from '@components/connection/loggedin-block';
import { InlinePrice } from '@components/inline-price';

import { formatPrice } from '@helpers/wording';

import { MarketingType } from '@constants/plan';

import { getPromoDuration } from '@promo-simulator/helpers';
import { OnClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

import { TicketLine } from './ticket-line';

type CartProps = {
    title?: string;
    hasSubtitles?: boolean;
    showTotal?: boolean;
    cart: SimulatedCart;
    virtualCart: SimulatorCart;
    impacts?: ImpactsSimulation;
    isLoading: boolean;
    simulatorPlans: PlanType[];
    onValidate?: (e: OnClickEvent) => void;
    hideRef?: RefCallback<HTMLElement>;
    simulation?: ImpactsSimulation;
    hostPage?: 'LP_TOUTATIS' | 'PACK_FAI';
    discount?: number;
    href?: string;
};

export function Ticket({
    title,
    hasSubtitles = false,
    showTotal = true,
    cart,
    isLoading,
    simulatorPlans,
    onValidate,
    hideRef,
    simulation,
    discount,
    href,
}: Readonly<CartProps>) {
    const getAllProductsFromSimulatedCart = (cart: SimulatedCart): Product[] => {
        return cart.quotes?.flatMap((quote) => quote.products) || [];
    };

    const products = getAllProductsFromSimulatedCart(cart);

    const getTotalTicketLine = () => {
        const cartPlans = simulatorPlans.filter((plan) =>
            getAllProductsFromSimulatedCart(cart)
                .map((p) => p.gencode)
                .includes(plan.gencode),
        );

        let maxDuration = 0;
        let planWithMaxDuration = null;
        if (cartPlans) {
            for (const plan of cartPlans) {
                const duration = getPromoDuration(plan?.details?.discounts);
                if (duration >= maxDuration && plan.obligation !== 'none') {
                    maxDuration = duration;
                    planWithMaxDuration = plan;
                }
            }
        }

        const allHomePromos = products.flatMap((plan) => {
            const simulatorPlan = simulatorPlans.find((simulatorPlan) => simulatorPlan.gencode === plan.gencode);
            if (simulatorPlan) {
                return filterPromotionsByTypesMarketing(
                    simulatorPlan?.details?.discounts || [],
                    [MarketingType.HOME],
                    false,
                    true,
                );
            }
        });
        const totalPromoAmount = allHomePromos.reduce((acc, promo) => acc + (promo?.value || 0), 0);
        return (
            <TicketLine
                title='Total'
                basePrice={cart.price.monthly.prices.initial}
                price={cart.price.monthly.prices.final}
                priceDuration={maxDuration}
                obligationLabel={planWithMaxDuration?.obligationLabel}
                period='mois'
                promoName={`B.iG économies : -${formatPrice(totalPromoAmount)}/mois déjà déduits`}
                promoAlt={`Big économies : ${formatPrice(totalPromoAmount)} par mois déjà déduits`}
                promoAmount={totalPromoAmount}
                isLoading={isLoading}
            />
        );
    };

    const getDiscountLine = () => {
        const familyContract = getFamilyContract(simulation);
        if (!familyContract || !familyContract.length) {
            return <Spacer size={SpacerSize.FIVE} />;
        }
        return (
            <>
                <Divider />
                <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                    Et vous économisez sur ces forfaits&nbsp;:
                </Text>
                <Rows gap={2}>
                    {familyContract.map((contract) => (
                        <CurrentContract contract={contract} key={`ticket-discount-${contract.id}`} />
                    ))}
                </Rows>
                <Spacer size={SpacerSize.TWO} />
            </>
        );
    };

    function getMonthlyProducts() {
        return products.filter((product) => product.details.price.type === 'MENSUEL');
    }

    function getTodayProducts() {
        return products.filter((product) => product.details.price.type === 'IMMEDIAT');
    }

    const monthlyProducts = getMonthlyProducts();
    const immediateProducts = getTodayProducts();
    const immediatePrice = immediateProducts.reduce((acc, product) => acc + product.details.price.final, 0);
    return (
        <div id='simulation-ticket'>
            {title ? (
                <Title level={TitleLevels.THREE} inverted>
                    {title}
                </Title>
            ) : null}
            <Box>
                <BoxContent className={isLoading ? 'is-loading' : ''}>
                    <Rows className='has-text-left'>
                        {hasSubtitles && immediateProducts.length > 0 ? (
                            <>
                                <Row>
                                    <Columns mobile>
                                        <Column>
                                            <Title
                                                level={TitleLevels.FIVE}
                                                className='has-text-left is-flex is-spaced-between'
                                                marginless
                                            >
                                                À payer à la commande
                                            </Title>
                                        </Column>
                                        <Column narrow>
                                            <Title
                                                level={TitleLevels.FIVE}
                                                className='has-text-left is-flex is-spaced-between'
                                                marginless
                                            >
                                                {formatPrice(immediatePrice)}
                                            </Title>
                                        </Column>
                                    </Columns>
                                </Row>
                                {immediateProducts?.map((product) => {
                                    return (
                                        <TicketLine
                                            key={`line-${product.gencode}-${product.quoteIndex}`}
                                            title={product.name}
                                            basePrice={product.details.price.initial}
                                            price={product.details.price.initial}
                                            isBold={!hasSubtitles}
                                        />
                                    );
                                })}
                                {discount ? (
                                    <TicketLine
                                        key='line-discount'
                                        title='Remise totale'
                                        basePrice={0}
                                        price={-discount}
                                        isBold={!hasSubtitles}
                                    />
                                ) : null}
                                <Row>
                                    <Divider marginless />
                                </Row>
                            </>
                        ) : null}
                        {hasSubtitles && cart.price.monthly.prices.initial > 0 && (
                            <Row>
                                <Columns mobile>
                                    <Column>
                                        <Title
                                            level={TitleLevels.FIVE}
                                            className='has-text-left is-flex is-spaced-between'
                                            marginless
                                        >
                                            À payer par mois
                                        </Title>
                                    </Column>
                                    <Column narrow>
                                        <Text
                                            level={TextLevels.ONE}
                                            marginless
                                            typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                                        >
                                            <InlinePrice
                                                level={TextLevels.TWO}
                                                amount={cart.price.monthly.prices.final}
                                                period='mois'
                                            />
                                        </Text>
                                    </Column>
                                </Columns>
                            </Row>
                        )}

                        {(hasSubtitles ? monthlyProducts : products).map((product) => {
                            const period = product.details.price.type === 'MENSUEL' ? 'mois' : undefined;

                            const plan = simulatorPlans.find((plan) => plan.gencode === product.gencode);
                            const monthlyPromotion = filterPromotionsByTypesMarketing(
                                plan?.details?.discounts || [],
                                [MarketingType.HOME],
                                false,
                                true,
                            )[0];

                            const promoDuration = getPromoDuration(plan?.details?.discounts);

                            let dataCy: string | undefined;

                            if (plan) {
                                const isFai = plan.categories.includes('fai');
                                dataCy = isFai ? `plan-fai` : `plan-mobile`;
                            }

                            return (
                                <TicketLine
                                    key={`line-${product.gencode}-${product.quoteIndex}`}
                                    data-cy={dataCy}
                                    title={product.name}
                                    basePrice={product.details.price.forever}
                                    price={product.details.price.final}
                                    promoName={
                                        plan?.details.discounts.find((d) =>
                                            d.marketingTypes.includes(MarketingType.HOME),
                                        )?.pennant
                                    }
                                    promoAmount={monthlyPromotion?.value}
                                    priceDuration={promoDuration}
                                    period={period}
                                    obligationLabel={plan?.obligationLabel}
                                    isBold={!hasSubtitles}
                                    isLoading={isLoading}
                                />
                            );
                        })}
                    </Rows>
                    {showTotal && (
                        <>
                            <Divider />
                            <Rows>{getTotalTicketLine()}</Rows>
                        </>
                    )}
                    {getDiscountLine()}
                    <Rows>
                        <Row>
                            <div ref={hideRef}>
                                <Button
                                    id='disableSticky'
                                    onClick={!!onValidate ? onValidate : undefined}
                                    variant={ButtonVariant.CONVERSION}
                                    disabled={isLoading}
                                    fullwidth
                                    name={cart.quotes?.length > 2 ? 'Continuer' : 'Tester mon éligibilité'}
                                    type={href ? undefined : ButtonType.BUTTON}
                                    href={href}
                                    markup={href ? ButtonMarkup.A : ButtonMarkup.BUTTON}
                                >
                                    {cart.quotes?.length > 2 ? 'Continuer' : 'Tester mon éligibilité'}
                                </Button>
                            </div>
                        </Row>
                    </Rows>
                </BoxContent>
            </Box>
        </div>
    );
}
